import { Overlay } from 'components/Disclaimer/Disclaimer.styled';
import TrayTotal from 'components/Tray/TrayTotal/TrayTotal';
// import TrayFinance from 'components/Tray/TrayFinance/TrayFinance';
import Label from 'components/_utility/Label';
import { getGarageEmailAddress } from 'helpers/garage';
import fetchGoal from 'helpers/goals';
import { getPackTotal } from 'helpers/packs';
import request from 'helpers/request';
import { useNavigate } from 'helpers/routing';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ChevronLeft from 'src/assets/icons/chevron-left.svg';
import ChevronRight from 'src/assets/icons/chevron-right-thick.svg';
import Burger from 'src/assets/icons/burger.svg';
import { bodyNoScroll, getViewportWidth, interceptLinkClick, updateLinkSubmit } from 'src/helpers/utilities';
import useBikeTotal from 'src/hooks/useBikeTotal';
import useChooseColour from 'src/hooks/useChooseColour';
import useDataLayer from 'src/hooks/useDataLayer';
import usePrice from 'src/hooks/usePrice';
import useSaveToGarage from 'src/hooks/useSaveToGarage';
import useSteps from 'src/hooks/useSteps';
import {
  ctaSelector,
  disclaimerSelector,
  getGoalId,
  getSendToDealerData,
  getProvincePricingEnabled,
} from 'src/redux/app/selectors';
import {
  clearPreviewedPack,
  closeAccessoryResolution,
  removePack,
  removeRequiredAccessories,
  resolveAccessoriesRequired,
  resolveAccessoryConflict,
  resolveAccessoryPackConflict,
  resolvePackAccessoryConflict,
  selectPack,
  setPreviewedPack,
} from 'src/redux/bike/actions';
import {
  bikeColourSelector,
  bikeModelSelector,
  getAccessoryPendingRemoval,
  getConflictChoice,
  getConflictingAccessories,
  getPack,
  getPackPendingRemoval,
  getPackSelectedAccessories,
  getPendingAccessories,
  getPendingPack,
  getPreviewedPack,
  getRequiresAccessories,
  getRequiresSingleChoice,
} from 'src/redux/bike/selectors';
import { closeModal, openModal, setPrintActions, setScrollYPosition, setStep } from 'src/redux/configurator/actions';
import {
  accessoryItemsSelector,
  currentStepSelector,
  disclaimerTypeSelector,
  openModalSelector,
  getSelectedProvince,
} from 'src/redux/configurator/selectors';
import { setConfigurations, setSaving } from 'src/redux/garage/actions';
import { getConfigurations, getSaving, getLoadedConfiguration } from 'src/redux/garage/selectors';
import { getInteractions } from 'src/redux/tagging/selectors';
import useOpenFinance from 'src/hooks/useOpenFinance';
import useLaunchSendToDealer from 'src/hooks/useLaunchSendToDealer';
import {
  BoxShadow,
  ConfigOptionsModalButton,
  Container,
  FinanceOptionsContainer,
  PrevNextButton,
  PrevNextLinkA,
  PrevNextText,
} from './Tray.styled';
import TrayOptions, { TrayOptionsPortal } from './TrayOptions/TrayOptions';

const Tray = ({ scriptsLoadedSuccessfully, hideOnDesktop, isSimulationChecksumExists, openShareModal }) => {
  const pushToDataLayer = useDataLayer();
  const navigate = useNavigate();
  const steps = useSteps();
  const total = useBikeTotal();
  const formatPrice = usePrice();
  const saveToGarage = useSaveToGarage();
  const chooseColour = useChooseColour();
  const openFinance = useOpenFinance();
  const launchSendToDealer = useLaunchSendToDealer();
  const dispatch = useDispatch();
  const [isScrolling, setIsScrolling] = useState(false);

  const trayModalOpen = (useSelector(openModalSelector) || {}).tray || false;
  const activeStep = useSelector(currentStepSelector);
  const disclaimerType = useSelector(disclaimerTypeSelector);
  const ctaLink = useSelector(ctaSelector);
  const { dynamicsForm, findADealer } = ctaLink;
  const bikeModel = useSelector(bikeModelSelector);
  const disclaimerInformation = useSelector(disclaimerSelector);
  const provincePricingEnabled = useSelector(getProvincePricingEnabled);
  const selectedProvince = useSelector(getSelectedProvince);
  const accessoryPendingRemoval = useSelector(getAccessoryPendingRemoval);
  const accessories = useSelector(accessoryItemsSelector);
  const conflictingAccessories = useSelector(getConflictingAccessories);
  const { requiresAll: requiresAllAccessories, requiresSingle: requiresSingleAccessory } = useSelector(
    getRequiresAccessories
  );
  const conflictChoice = useSelector(getConflictChoice);
  const requiresSingleChoice = useSelector(getRequiresSingleChoice);
  const pendingAccessories = useSelector(getPendingAccessories);
  const previewedPack = useSelector(getPreviewedPack);
  const pack = useSelector(getPack);
  const pendingPack = useSelector(getPendingPack);
  const packPendingRemoval = useSelector(getPackPendingRemoval);
  const packSelectedAccessories = useSelector(getPackSelectedAccessories);
  const interactions = useSelector(getInteractions);
  const garageSaving = useSelector(getSaving);
  const sendToDealerData = useSelector(getSendToDealerData);
  const bikeColour = useSelector(bikeColourSelector);
  const loadedConfiguration = useSelector(getLoadedConfiguration);

  const primaryCTAOverride =
    bikeModel.configSummaryPrimaryCTAOverride &&
    updateLinkSubmit({
      link: bikeModel.configSummaryPrimaryCTAOverride.url,
      productCode: bikeModel?.productCode,
      colourCode: bikeColour?.colourCode,
      configurationID: loadedConfiguration?.id,
    });
  const primaryCTAOverrideText =
    bikeModel.configSummaryPrimaryCTAOverride && bikeModel.configSummaryPrimaryCTAOverride.text;
  // const primaryCTAOverrideTarget =
  //   (bikeModel.configSummaryPrimaryCTAOverride && bikeModel.configSummaryPrimaryCTAOverride.target) || '_self';

  const showConflictingAccessories = Boolean(conflictingAccessories.length);
  const showRequiredAccessories =
    !conflictingAccessories.length && (requiresSingleAccessory.length || requiresAllAccessories.length);

  const activeStepData = steps.find(step => step.number === activeStep);
  const previousStepData = steps.find(step => step.number === activeStep - 1);
  const nextStepData = steps.find(step => step.number === activeStep + 1);
  const stepsMapped = steps.map(step => step.length);
  const stepCount = Object.keys(stepsMapped).length;

  const configurations = useSelector(getConfigurations);

  const prevButton = useRef();
  const nextButton = useRef();

  const userEmail = window.localStorage.getItem('garageEmailAddress');
  const saveGoalId = useSelector(getGoalId('save configuration to garage'));
  const testRideGoalId = useSelector(getGoalId('cta - Test ride - summary page'));

  useEffect(() => {
    const fetchConfigurations = async () => {
      const { data } = await request(`/api/configurator/garage?user=${userEmail}`);

      dispatch(setConfigurations(data ? data.configurations : []));
    };

    if (userEmail) {
      fetchConfigurations();
    }
  }, [dispatch, userEmail]);

  useEffect(() => {
    const prevButtonEl = prevButton.current;
    const nextButtonEl = nextButton.current;

    if (prevButtonEl && nextButtonEl) {
      prevButtonEl.blur();
      nextButtonEl.blur();
    }
  }, [activeStep]);

  // Have to check if the user is on a Mac, as chrome would align the tray chevrons differently across windows
  const isMac = navigator.platform.indexOf('Mac') > -1;
  const testBikeBaseUrl = ctaLink?.testRide?.includes('ColourCode')
    ? ctaLink?.testRide
    : ctaLink?.testRide + bikeModel?.familyId;
  const bikeLegacy = bikeModel?.canTestRide && ctaLink?.testRide ? testBikeBaseUrl : null;
  const testDriververride = bikeModel?.testRideOverride;
  const testDriverButtonUrl = testDriververride ? testDriververride.url : bikeLegacy;

  const financeHidden =
    !scriptsLoadedSuccessfully ||
    (typeof window.scfSimulation !== 'object' && typeof window.codeweavers === 'undefined') ||
    !bikeModel.canFinance ||
    !ctaLink.finance;

  const options = [
    {
      id: 'save',
      label: 'saveLabel',
      icon: '/images/configurator/config-tray/Save.svg',
      onClick: async () => [
        pushToDataLayer('configuratorSaveAttempt'),
        getGarageEmailAddress() ? [await fetchGoal(saveGoalId), saveToGarage()] : dispatch(openModal('saveToGarage')),
      ],
      disabled: garageSaving,
    },
    {
      id: 'garage',
      label: 'garageLabel',
      icon: '/images/configurator/config-tray/Garage.svg',
      count: configurations.length,
      onClick: () => {
        pushToDataLayer('configuratorGarageAttempt');

        if (interactions.total) {
          return dispatch(openModal('unsavedConfiguration'));
        }

        if (!getGarageEmailAddress()) {
          return dispatch(openModal('loadGarage'));
        }

        navigate('/garage');
      },
    },
    {
      id: 'testRideLabel',
      label: 'testRideLabel',
      icon: '/images/configurator/config-tray/Bike.svg',
      target: testDriververride ? testDriververride.target : '_self',
      link: bikeModel.canTestRide
        ? updateLinkSubmit({
            link: testDriverButtonUrl,
            productCode: bikeModel?.productCode,
            colourCode: bikeColour?.colourCode,
          })
        : null,
      onClick: async event => [
        await fetchGoal(testRideGoalId),
        interceptLinkClick(event, () => pushToDataLayer('configuratorTestRide')),
      ],
    },
    {
      id: 'financeLabel',
      label: 'financeLabel',
      icon: '/images/configurator/config-tray/Calculator.svg',
      onClick: () => openFinance(),
      disabled: !isSimulationChecksumExists,
      hidden: financeHidden,
    },
    {
      id: 'shareLabel',
      label: 'shareLabel',
      icon: '/images/configurator/config-tray/Share.svg',
      onClick: () => [pushToDataLayer('configuratorShareAttempt'), openShareModal()],
    },
  ];

  const openTrayModal = () => {
    bodyNoScroll.add();
    dispatch(openModal('tray'));
  };

  const closeTrayModal = () => {
    bodyNoScroll.remove();
    dispatch(closeModal('tray'));
  };

  const awaitingConflictChoice = (showConflictingAccessories || pendingPack) && !conflictChoice;
  const awaitingRequiredChoices = showRequiredAccessories && requiresSingleAccessory.length && !requiresSingleChoice;
  const resolvingAccessories = showConflictingAccessories || showRequiredAccessories || accessoryPendingRemoval;
  const visiblePack = pack || previewedPack;
  const awaitingPackChoice = visiblePack && activeStepData.packs;

  const handlePrevClick = () => {
    if (activeStepData.packs) {
      dispatch(clearPreviewedPack());
    }

    if (
      showRequiredAccessories ||
      showConflictingAccessories ||
      accessoryPendingRemoval ||
      pendingPack ||
      packPendingRemoval
    ) {
      return dispatch(closeAccessoryResolution());
    }

    if (activeStep - 1 === 1) {
      return dispatch(openModal('unsavedConfigurationAbandon'));
    }

    if (previewedPack) {
      return dispatch(setPreviewedPack(null));
    }

    dispatch(setPrintActions(0));
    dispatch(setScrollYPosition(0));
    dispatch(setStep(activeStep - 1));
    pushToDataLayer(previousStepData.gtmEvent);
  };

  const handleNextClick = () => {
    if (packPendingRemoval && pendingAccessories.length) {
      return dispatch(resolveAccessoryPackConflict());
    }

    if (pendingPack) {
      return dispatch(resolvePackAccessoryConflict());
    }

    if (packPendingRemoval) {
      return dispatch(removePack());
    }

    if (awaitingPackChoice) {
      pushToDataLayer('configuratorPackSelected', {
        configuratorPackName: visiblePack.name,
        configuratorPackID: visiblePack.id,
        configuratorPackPrice: getPackTotal(visiblePack),
      });

      dispatch(setScrollYPosition(0));
      dispatch(selectPack({ pack: visiblePack, selectedAccessories: packSelectedAccessories }));
      chooseColour(bikeColour.id);
    }

    if (accessoryPendingRemoval) {
      return dispatch(removeRequiredAccessories());
    }

    if (showRequiredAccessories) {
      return dispatch(resolveAccessoriesRequired({ accessories }));
    }

    if (showConflictingAccessories) {
      const chosenAccessory = conflictChoice === 'new' ? pendingAccessories[0] : conflictingAccessories[0];

      pushToDataLayer('configuratorAccessoryConflict', {
        configuratorAccessoryID: chosenAccessory.partNumber,
        configuratorAccessoryName: chosenAccessory.name,
        configuratorAccessoryPrice: chosenAccessory.price,
      });

      return dispatch(resolveAccessoryConflict());
    }

    if (activeStep + 1 <= stepCount) {
      dispatch(setPrintActions(0));
      dispatch(setScrollYPosition(0));
      pushToDataLayer(nextStepData.gtmEvent);
      dispatch(setStep(activeStep + 1));
    }

    if (activeStep + 1 > stepCount) {
      if (dynamicsForm?.url) {
        return window.open(dynamicsForm.url, dynamicsForm?.target || '_blank');
      }
      launchSendToDealer();
    }
  };

  const renderNextLabel = () => {
    if (!nextStepData) {
      if (dynamicsForm?.url && dynamicsForm?.text) {
        return <Label name={dynamicsForm.text} />;
      }
      return <Label name={ctaLink.findADealer ? 'findADealerLabel' : 'sendToDealerLabel'} />;
    }

    if (awaitingPackChoice || conflictingAccessories.length) {
      return <Label name="confirmLabel" />;
    }

    if (accessoryPendingRemoval || packPendingRemoval) {
      return <Label name="removeLabel" />;
    }

    if ((!accessoryPendingRemoval && !packPendingRemoval && showRequiredAccessories) || pendingPack) {
      return <Label name="addLabel" />;
    }

    return <Label name="nextLabel" />;
  };

  const viewportWidth = getViewportWidth();
  const isMobile = viewportWidth < 768;

  useEffect(() => {
    let scrollTimeout;

    const handleScroll = () => {
      setIsScrolling(true);
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
      }, 300);
    };

    if (isMobile) {
      window.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (isMobile) {
        window.removeEventListener('scroll', handleScroll);
        clearTimeout(scrollTimeout);
      }
    };
  }, []);

  const goToPage = () => {
    dispatch(setSaving(false));
    window.__configuratorRedirectTarget = true;
    const target = bikeModel.configSummaryPrimaryCTAOverride.target || '_self';
    window.open(primaryCTAOverride, target);
  };

  const getSendToDealerLabel = () => {
    if (primaryCTAOverride) {
      return primaryCTAOverrideText;
    }
    if (dynamicsForm?.url && dynamicsForm?.text) {
      return dynamicsForm.text;
    }
    return findADealer ? 'findADealerLabel' : 'sendToDealerLabel';
  };

  const checkFindOrSendToDealer = () => {
    if (getGarageEmailAddress() && primaryCTAOverride) {
      return goToPage();
    }
    if (dynamicsForm?.url) {
      window.open(dynamicsForm.url, dynamicsForm.target);
    } else {
      launchSendToDealer(goToPage);
    }
  };

  if (provincePricingEnabled && activeStep + 1 > stepCount && !selectedProvince) {
    return null;
  }

  return (
    <Container isScrolling={isScrolling} hideOnDesktop={hideOnDesktop}>
      {disclaimerType && <Overlay />}
      <BoxShadow></BoxShadow>
      <PrevNextButton onClick={handlePrevClick} ref={prevButton}>
        <PrevNextText isMac={isMac}>
          <ChevronLeft />
          {resolvingAccessories || packPendingRemoval || pendingPack ? (
            <Label name={activeStepData.label} />
          ) : (
            <Label name="previousLabel" />
          )}
        </PrevNextText>
      </PrevNextButton>

      <ConfigOptionsModalButton hidden={!nextStepData} onClick={openTrayModal}>
        <Burger />
      </ConfigOptionsModalButton>

      <TrayOptions options={options} />

      {trayModalOpen ? <TrayOptionsPortal options={options} onCloseTrayModal={closeTrayModal} modalOpen /> : null}

      <FinanceOptionsContainer
        financeHidden
        // @TODO: Find out if additional finance button should be removed permanently
        // financeHidden={financeHidden}
      >
        <TrayTotal
          // @TODO: Find out if additional finance button should be removed permanently
          // financeHidden={financeHidden}
          financeHidden={financeHidden || !isSimulationChecksumExists}
          label="totalLabel"
          price={formatPrice(total)}
          text={disclaimerInformation.price}
          isScrolling={isScrolling}
        />
        {/* @TODO: Find out if additional finance button should be removed permanently */}
        {/* {!financeHidden && <TrayFinance financeLabel="financeLabel" calculateLabel="calculateLabel" />} */}
      </FinanceOptionsContainer>
      {activeStep + 1 > stepCount && ctaLink.findADealer && !sendToDealerData.enabled ? (
        <PrevNextLinkA onClick={() => [pushToDataLayer('configuratorSumPrim'), checkFindOrSendToDealer()]}>
          <PrevNextText nextbtn isMac={isMac}>
            <Label name={getSendToDealerLabel()} /> <ChevronRight />
          </PrevNextText>
        </PrevNextLinkA>
      ) : (
        <PrevNextButton
          nextBtn
          isLastStep={!nextStepData}
          // @TODO: Find out if additional finance button should be removed permanently
          // financeHidden={financeHidden}
          financeHidden
          onClick={handleNextClick}
          disabled={awaitingConflictChoice || (awaitingRequiredChoices && requiresSingleAccessory.length > 1)}
          ref={nextButton}
          primary
        >
          <PrevNextText
            nextBtn
            isMac={isMac}
            isLastStep={!nextStepData}
            // @TODO: Find out if additional finance button should be removed permanently
            // financeHidden={financeHidden}
            financeHidden
          >
            <span>{renderNextLabel()}</span>
            <ChevronRight />
          </PrevNextText>
        </PrevNextButton>
      )}
    </Container>
  );
};

export default Tray;
